import {
  ref,
  onMounted,
  defineComponent,
  computed,
  watch,
} from "@vue/composition-api";

import { v4 as uuidv4 } from "uuid";
import { TOAST_OPTIONS } from "@/constants";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import BuildTruckLoadForm from "@/components/TruckLoads/BuildTruckLoadForm.vue";
// import BuildTruckLoadInventoryLots from "@/components/TruckLoads/BuildTruckLoadInventoryLots.vue";
// import BuildTruckLoadSellContracts from "@/components/TruckLoads/BuildTruckLoadSellContracts.vue";
// import BuildTruckLoadLineItems from "@/components/TruckLoads/BuildTruckLoadLineItems.vue";

import SellContracts from "@/components/SellContracts/SellContracts.vue";
import InventoryLots from "@/components/InventoryLots/InventoryLots.vue";
import LineItems from "@/components/LineItems/LineItems.vue";

import { LineItem } from "@/store/modules/lineItems";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";
import { truckLoadsController } from "@/controllers/truckLoads";
import { sellContractsController } from "@/controllers/sellContracts";

import { format } from "date-fns";

import {
  TruckLoad,
  FormTruckLoad,
  buildFormTruckLoad,
} from "@/models/TruckLoad";

import {
  buildFormIlsctlLineItems,
  FormIlsctlLineItem,
} from "@/models/IlsctlLineItem";
import { buildFormTruckLoadLineItems } from "@/models/TruckLoadLineItem";

import { InventoryLot } from "@/models/InventoryLot";
import { SellContract } from "@/models/SellContract";

import { JSConn } from "@/utilities/js-conn";
import { NamedRts } from "@/router";
import { LotUnit } from "@/store/modules/lotUnits";
import { WeightQuantityPriceStatus } from "@/models/WeightQuantityPriceStatus";

import Big from "big.js";
import { ValueChanger } from "@/models/ValueChanger";

const enum ShowableItems {
  sellContracts = "sellContracts",
  inventoryLots = "inventoryLots",
  lineItems = "lineItems",
}

export default defineComponent({
  name: "buildTruckLoad",
  components: {
    LoadingAnimation,
    BuildTruckLoadForm,
    // BuildTruckLoadInventoryLots,
    // BuildTruckLoadSellContracts,
    // BuildTruckLoadLineItems,

    SellContracts,
    InventoryLots,
    LineItems,
  },

  props: {
    truckLoadId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    const { formTruckLoad, loadTruckLoadIntoForm } = buildFormTruckLoad();

    const dataLoaded = ref(false);
    const loadingMessage = ref("Preparing the form...");

    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleteTruckLoad = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.truckLoadId,
      };

      truckLoadsController.deleteTruckLoad(jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.truckLoads,
        });
        return Promise.resolve();
      });
    };

    const tlDestination = ref<SellContract | null>(null);

    // The app needs to know the weight lot units for the truck load
    // and the Destination
    let tlWeightLotUnit: LotUnit | null = null;

    let destWeightLotUnit: LotUnit | null = null;

    const calcTlWeight = computed(() => {
      return new Big(formTruckLoad.gross_weight).minus(
        new Big(formTruckLoad.tare_weight),
      );
    });

    // Which items are going to be shown?
    // There are several options
    // Allow the user to manually select the one they want
    // also let the app guess the probable one

    // keep track of the destination, if there is one
    // show inventory lots,
    // if not, show sell contracts
    watch(
      () => tlDestination.value,
      (currentValue: SellContract | null, _prevValue: SellContract | null) => {
        if (currentValue) {
          destWeightLotUnit = $store.getters.lotUnitById(
            currentValue.weight_lot_unit_id,
          );
          if (itemsToShow.value === ShowableItems.sellContracts) {
            itemsToShow.value = ShowableItems.inventoryLots;
          }
        }
      },
    );

    watch(
      () => formTruckLoad.weight_lot_unit_id,
      (currentValue: string, _prevValue) => {
        if (currentValue !== "") {
          tlWeightLotUnit = $store.getters.lotUnitById(currentValue);
        }
      },
    );

    const itemsToShow = ref(ShowableItems.sellContracts);

    const setItemsToShow = (newValue: ShowableItems) => {
      itemsToShow.value = newValue;
    };

    let { submitButtonText, submitting } = buildSubmitButton();

    let {
      formIlsctlLineItems,
      addIlsctlLineItem,
      loadIlsctlLineItemsIntoForm,
    } = buildFormIlsctlLineItems();

    let {
      formTruckLoadLineItems,
      addTruckLoadLineItem,
      loadTruckLoadLineItemsIntoForm,
    } = buildFormTruckLoadLineItems();

    const truckLoad = computed(() => {
      return $store.getters.truckLoad as TruckLoad;
    });

    const sellContract = computed(() => {
      return $store.getters.sellContract as SellContract;
    });

    const receiveLineItem = (ln: LineItem) => {
      addTruckLoadLineItem(formTruckLoadLineItems);
      const td = format(new Date(), "yyyy-MM-dd");

      const tmpLastItem =
        formTruckLoadLineItems[formTruckLoadLineItems.length - 1];
      tmpLastItem.item_date = td;
      tmpLastItem.line_item_id = ln.id;
      tmpLastItem.quantity_sync = "sc_weight";
      tmpLastItem.rate = ln.default_rate;
      tmpLastItem.sort_order = 200;
      tmpLastItem.formLineItem.id = ln.id;
      tmpLastItem.formLineItem.label = ln.description;

      // loop through the ilsctl items
      // if destination is weight_price add up weight
      // else add up sc quantity
      if (tlDestination.value) {
        if (
          tlDestination.value.weight_sell_price_status ===
          WeightQuantityPriceStatus.actual
        ) {
          const tmpItemsAccum = formIlsctlLineItems.reduce(
            (accum: Big, item) => {
              return accum.plus(new Big(item.weight));
            },
            new Big(0),
          );

          tmpLastItem.quantity = Number(tmpItemsAccum);
        } else {
          const tmpItemsAccum = formIlsctlLineItems.reduce(
            (accum: Big, item) => {
              return accum.plus(new Big(item.sc_quantity));
            },
            new Big(0),
          );

          tmpLastItem.quantity = Number(tmpItemsAccum);
        }
      } else {
        // no destination
        tmpLastItem.quantity = 1;
      }
    };

    const receiveInventoryLot = (il: InventoryLot) => {
      addIlsctlLineItem(formIlsctlLineItems);
      const tmpLastItem = formIlsctlLineItems[formIlsctlLineItems.length - 1];
      tmpLastItem.average_weight_lot_unit_id = formTruckLoad.weight_lot_unit_id;

      tmpLastItem.il_quantity = 1;
      tmpLastItem.purchase_price = il.purchase_price;
      tmpLastItem.purchase_price_status = il.purchase_price_status;

      tmpLastItem.formInventoryLot.label = il.label;
      tmpLastItem.sell_price = il.default_sell_price;
      tmpLastItem.inventory_lot_id = il.id;

      tmpLastItem.sc_quantity = 1;

      tmpLastItem.status = formTruckLoad.status;

      // get the weight from the truck load
      // if it is an amount above zero
      // the app is going to assume it is correct
      // are the log units the same?
      if (tlWeightLotUnit && destWeightLotUnit) {
        // neither one is null
        if (tlWeightLotUnit.id === destWeightLotUnit.id) {
          // the lot units are the same
          tmpLastItem.weight = Number(calcTlWeight);
        } else {
          // if not, make the correct calculations
          tmpLastItem.weight = Number(
            calcTlWeight.value
              .div(new Big(tlWeightLotUnit.conversion_factor))
              .times(new Big(destWeightLotUnit.conversion_factor)),
          );
        }

        // there was a weight lot unit for both truck loads
        // and the sell contract
        tmpLastItem.weight_status = WeightQuantityPriceStatus.actual;
      } else {
        // something went wrong
        // just do the best we can
        tmpLastItem.weight = 1;
        tmpLastItem.weight_status = WeightQuantityPriceStatus.estimated;
      }

      tmpLastItem.weight_purchase_price = il.weight_purchase_price;
      tmpLastItem.weight_purchase_price_status =
        il.weight_purchase_price_status;

      tmpLastItem.lot_unit_id = il.lot_unit_id;
      loadDestinationForItems();
    };

    const receiveSellContract = (sc: SellContract) => {
      tlDestination.value = sc;
      loadDestinationForItems();
    };

    const receiveFormSubmit = (frmItem: FormTruckLoad) => {
      frmItem.ilsctl_line_items_attributes = [...formIlsctlLineItems];
      frmItem.truck_load_line_items_attributes = [...formTruckLoadLineItems];

      const jsConn = new JSConn();

      submitButtonText.value = "Submitting...";
      submitting.value = true;

      let apiResponse;

      if (frmItem.id === "") {
        frmItem.id = uuidv4();
        jsConn.formParams = { ...frmItem };
        apiResponse = truckLoadsController.createTruckLoad(jsConn);
      } else {
        jsConn.formParams = { ...frmItem };
        apiResponse = truckLoadsController.updateTruckLoad(jsConn);
      }

      apiResponse
        .then(() => {
          ctx.root.$bvToast.toast(
            "Truck load successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push({
            name: NamedRts.truckLoads,
          });
        })
        .catch(() => {
          // TODO show the errors here
        })
        .finally(() => {
          submitButtonText.value = "Submit";
          submitting.value = false;
        });
    };

    const loadDestinationForItems = () => {
      if (tlDestination.value) {
        for (const item of formIlsctlLineItems) {
          item.formSellContract.id = tlDestination.value.id;
          item.formSellContract.clientLabel = tlDestination.value.client_label;
          item.formSellContract.commodityLabel =
            tlDestination.value.commodity_label;
          item.formSellContract.label = tlDestination.value.label;
          item.formSellContract.lotUnitLabel =
            tlDestination.value.weight_lot_unit_label;

          item.sell_contract_id = tlDestination.value.id;
          item.sell_price = tlDestination.value.sell_price;
          item.sell_price_status = tlDestination.value.sell_price_status;
          item.weight_lot_unit_id = tlDestination.value.weight_lot_unit_id;
          item.weight_sell_price = tlDestination.value.weight_sell_price;
          item.weight_sell_price_status =
            tlDestination.value.weight_sell_price_status;
        }
      }
    };

    const reloadTruckLoad = () => {
      let dataPromises = [];
      const jsConn = new JSConn();

      jsConn.formParams = {
        id: props.truckLoadId,
      };

      dataPromises.push(truckLoadsController.needTruckLoad(jsConn));

      return Promise.all(dataPromises);
    };

    // const scQuantityBlur = (ilsctlItem: FormIlsctlLineItem) => {
    //   if (tlDestination.value) {
    //     if (ilsctlItem.weight_lot_unit_id === tlDestination.value.lot_unit_id) {
    //       if (ilsctlItem.weight === 0) {
    //         ilsctlItem.weight = ilsctlItem.sc_quantity;
    //         ilsctlItem.weight_status = WeightQuantityPriceStatus.actual;
    //       }
    //     }
    //   }
    // };

    const changeIlQuantity = (
      { target }: { target: HTMLInputElement },
      item: FormIlsctlLineItem,
    ) => {
      item.il_quantity = Number(target.value);
      item.changerIlQuantity = ValueChanger.user;
      if (item.changerScQuantity === ValueChanger.app) {
        item.sc_quantity = item.il_quantity;
      }
    };

    const changeScQuantity = (
      { target }: { target: HTMLInputElement },
      item: FormIlsctlLineItem,
    ) => {
      item.sc_quantity = Number(target.value);
      item.changerScQuantity = ValueChanger.user;
      if (item.changerIlQuantity === ValueChanger.app) {
        item.il_quantity = item.sc_quantity;
      }
    };

    onMounted(() => {
      if (props.truckLoadId !== "new") {
        const jsConnSellContract = new JSConn();

        reloadTruckLoad()
          .then(() => {
            // load the data into the form.
            loadTruckLoadIntoForm(truckLoad.value, formTruckLoad);
            loadIlsctlLineItemsIntoForm(
              truckLoad.value.ilsctl_line_items,
              formIlsctlLineItems,
            );
            loadTruckLoadLineItemsIntoForm(
              truckLoad.value.truck_load_line_items,
              formTruckLoadLineItems,
            );
          })
          .then(() => {
            // set the first destination as the truck load destination
            if (truckLoad.value.ilsctl_line_items[0]) {
              jsConnSellContract.formParams = {
                id: truckLoad.value.ilsctl_line_items[0].sell_contract_id,
              };
              return sellContractsController.needSellContract(
                jsConnSellContract,
              );
            } else {
              return Promise.resolve("");
            }
          })
          .then(() => {
            // the correct sell contract should be in the store
            if (sellContract.value.label !== "") {
              tlDestination.value = sellContract.value;
            }
          })
          .finally(() => {
            dataLoaded.value = true;
          });
      } else {
        dataLoaded.value = true;
      }
    });

    return {
      dataLoaded,
      loadingMessage,

      formTruckLoad,
      truckLoad,

      receiveSellContract,

      receiveInventoryLot,

      receiveLineItem,

      receiveFormSubmit,

      formIlsctlLineItems,

      formTruckLoadLineItems,

      tlDestination,

      submitButtonText,
      submitting,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteTruckLoad,

      changeScQuantity,

      changeIlQuantity,

      itemsToShow,
      setItemsToShow,
    };
  },
});
